import {
    Avatar,
    Badge,
    ButtonBase,
    Divider,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
} from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';

import AuthenticatedPaths from '/b2b/routing/AuthenticatedRoutes/AuthenticatedRoutes.paths';
import HelpIcon from '@mui/icons-material/HelpOutline';
import Locale from '/b2b/common/components/Locale';
import LocalizedTooltip from '/b2b/common/components/LocalizedTooltip';
import { NAV_LABELS } from '../Sidebar/constants';
import NotificationList from '../NotificationList';
import NotificationStatus from '/b2b/notifications/components/NotificationStatus.container';
import NotificationsIcon from '@mui/icons-material/NotificationsOutlined';
import Permission from '/b2b/common/components/Permission';
import PropTypes from 'prop-types';
import SidebarFlyout from '../SidebarFlyout';
import UserDetails from '../UserDetails';
import classNames from 'classnames';
import makeStyles from '@mui/styles/makeStyles';
import noop from 'lodash/noop';
import styles from './SidebarFooter.styles';
import { translate } from '/b2b/common/helpers/i18n';
import { useLocation } from 'react-router-dom';

const useStyles = makeStyles(styles);

const getInitials = (name = '') =>
    name
        .split(' ')
        .map(part => part[0])
        .filter(Boolean)
        .slice(0, 2)
        .join('')
        .toUpperCase();

const SidebarFooter = props => {
    const {
        minified,
        notificationCount = 0,
        notificationsLimit,
        notificationsOpen,
        onCloseNotifications = noop,
        onFlyoutEnter = noop,
        onFlyoutLeave = noop,
        onOpenNotifications = noop,
        userName = '',
    } = props;
    const classes = useStyles(props);

    const notificationsAnchorRef = useRef(null);
    const accountAnchorRef = useRef(null);
    const [accountOpen, setAccountOpen] = useState(false);
    const location = useLocation();

    // The account flyout is local state, so (unlike the route-reset notifications
    // flyout) it has to be closed explicitly on navigation.
    useEffect(() => {
        setAccountOpen(false);
    }, [location.pathname]);

    // When the rail collapses out from under an open menu, close it so it doesn't
    // linger — detached and repositioned to the top of the page.
    useEffect(() => {
        if (minified) {
            setAccountOpen(false);
            onCloseNotifications();
        }
    }, [minified, onCloseNotifications]);

    // Keep the two flyouts mutually exclusive, and let a repeat click close the
    // open one (SidebarFlyout ignores clicks on its own anchor).
    const handleToggleNotifications = () => {
        if (notificationsOpen) {
            onCloseNotifications();
        } else {
            setAccountOpen(false);
            onOpenNotifications();
        }
    };
    const handleToggleAccount = () => {
        if (accountOpen) {
            setAccountOpen(false);
        } else {
            onCloseNotifications();
            setAccountOpen(true);
        }
    };

    return (
        <div className={classNames(classes.root, { [classes.minified]: minified })}>
            <NotificationStatus
                unreadNotifications={notificationCount}
                invisible
                limit={notificationsLimit}
            />
            <Divider className={classes.divider} />
            {minified ? (
                // Collapsed rail: notifications, help, and account stacked as icons.
                <List component="div" disablePadding>
                    <Permission toPerform="view" on={AuthenticatedPaths.NOTIFICATIONS}>
                        <ListItem
                            button
                            ref={notificationsAnchorRef}
                            className={classes.listItem}
                            onClick={handleToggleNotifications}
                            aria-label={translate(NAV_LABELS.notifications)}
                        >
                            <ListItemIcon className={classes.listItemIcon}>
                                <Badge
                                    badgeContent={
                                        notificationCount === 0 ? null : notificationCount
                                    }
                                    color="error"
                                    classes={{ badge: classes.notificationBadge }}
                                    invisible={notificationCount <= 0}
                                >
                                    <NotificationsIcon />
                                </Badge>
                            </ListItemIcon>
                            <ListItemText
                                classes={{ primary: classes.listItemText }}
                                primary={<Locale path={NAV_LABELS.notifications} />}
                            />
                        </ListItem>
                    </Permission>
                    <ListItem
                        button
                        id="btn-resource-center"
                        className={classes.listItem}
                        aria-label={translate(NAV_LABELS.helpCenter)}
                    >
                        <ListItemIcon className={classes.listItemIcon}>
                            <HelpIcon />
                        </ListItemIcon>
                        <ListItemText
                            classes={{ primary: classes.listItemText }}
                            primary={<Locale path={NAV_LABELS.helpCenter} />}
                        />
                    </ListItem>
                    <ListItem
                        button
                        ref={accountAnchorRef}
                        className={classes.listItem}
                        onClick={handleToggleAccount}
                        aria-label={translate('common.account')}
                    >
                        <ListItemIcon className={classes.listItemIcon}>
                            <Avatar className={classes.avatar}>{getInitials(userName)}</Avatar>
                        </ListItemIcon>
                        <ListItemText
                            classes={{ primary: classes.listItemText }}
                            primary={userName}
                        />
                    </ListItem>
                </List>
            ) : (
                // Expanded: a single row with the account on the left and the
                // notifications + help actions on the right.
                <div className={classes.accountRow}>
                    <ButtonBase
                        ref={accountAnchorRef}
                        className={classes.accountButton}
                        onClick={handleToggleAccount}
                        aria-label={translate('common.account')}
                    >
                        <Avatar className={classes.avatar}>{getInitials(userName)}</Avatar>
                        <span className={classes.accountName}>{userName}</span>
                    </ButtonBase>
                    <Permission toPerform="view" on={AuthenticatedPaths.NOTIFICATIONS}>
                        <LocalizedTooltip title={NAV_LABELS.notifications} placement="top">
                            <ButtonBase
                                ref={notificationsAnchorRef}
                                className={classes.iconAction}
                                onClick={handleToggleNotifications}
                                aria-label={translate(NAV_LABELS.notifications)}
                            >
                                <Badge
                                    badgeContent={
                                        notificationCount === 0 ? null : notificationCount
                                    }
                                    color="error"
                                    classes={{ badge: classes.notificationBadge }}
                                    invisible={notificationCount <= 0}
                                >
                                    <NotificationsIcon />
                                </Badge>
                            </ButtonBase>
                        </LocalizedTooltip>
                    </Permission>
                    <LocalizedTooltip title={NAV_LABELS.helpCenter} placement="top">
                        <ButtonBase
                            id="btn-resource-center"
                            className={classes.iconAction}
                            aria-label={translate(NAV_LABELS.helpCenter)}
                        >
                            <HelpIcon />
                        </ButtonBase>
                    </LocalizedTooltip>
                </div>
            )}

            {!minified && (
                <SidebarFlyout
                    anchorEl={notificationsAnchorRef.current}
                    open={!!notificationsOpen}
                    onClose={onCloseNotifications}
                    onMouseEnter={onFlyoutEnter}
                    onMouseLeave={onFlyoutLeave}
                    placement="top-end"
                >
                    <NotificationList onSelect={onCloseNotifications} limit={notificationsLimit} />
                </SidebarFlyout>
            )}

            {!minified && (
                <SidebarFlyout
                    anchorEl={accountAnchorRef.current}
                    open={accountOpen}
                    onClose={() => setAccountOpen(false)}
                    onMouseEnter={onFlyoutEnter}
                    onMouseLeave={onFlyoutLeave}
                    placement="top-start"
                >
                    <UserDetails />
                </SidebarFlyout>
            )}
        </div>
    );
};

SidebarFooter.propTypes = {
    minified: PropTypes.bool,
    notificationCount: PropTypes.number,
    notificationsLimit: PropTypes.number,
    notificationsOpen: PropTypes.bool,
    onCloseNotifications: PropTypes.func,
    onFlyoutEnter: PropTypes.func,
    onFlyoutLeave: PropTypes.func,
    onOpenNotifications: PropTypes.func,
    userName: PropTypes.string,
};

export default SidebarFooter;
